import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FileClock, Search, RefreshCw, Ticket as TicketIcon, AlertTriangle, Info, Terminal, X, Download, Shield, Copy, Check, ExternalLink, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import apiClient from '../../../lib/api';
import { cn } from '../../../lib/utils';
import { Pagination } from '../../../components/Pagination';

export interface AuditLogEntry {
  id: string;
  ts: string;
  level: 'info' | 'warn' | 'error' | 'security';
  category: 'ticket' | 'kb' | 'live_chat' | 'settings' | 'portal' | 'auth_rbac' | 'system' | string;
  action: string;
  entity_type: string;
  entity_id: string;
  ticket_id?: string | null;
  actor_id?: string | null;
  actor_type: 'agent' | 'customer' | 'system' | 'ai';
  target_label?: string | null;
  summary: string;
  details: Record<string, any>;
  ip?: string | null;
  user_agent?: string | null;
  session_id?: string | null;
  request_id?: string | null;
  // Compatibility fields
  actorName?: string | null;
  actorEmail?: string | null;
  actorRole?: string | null;
  createdAt?: string;
}

export interface AuditSummary {
  totalEvents: number;
  securityAlerts: number;
  systemErrors: number;
  warnings: number;
  infoCount: number;
  last24hEvents: number;
  categories: Record<string, number>;
}

export function SupportAuditLog() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [summary, setSummary] = useState<AuditSummary>({
    totalEvents: 0,
    securityAlerts: 0,
    systemErrors: 0,
    warnings: 0,
    infoCount: 0,
    last24hEvents: 0,
    categories: {},
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLogEntry | null>(null);
  const [relatedLogs, setRelatedLogs] = useState<AuditLogEntry[]>([]);
  const [isLiveStreaming, setIsLiveStreaming] = useState(true);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // URL-synced Filters
  const activeCategory = searchParams.get('category') || 'all';
  const activeLevel = searchParams.get('level') || 'all';
  const searchQuery = searchParams.get('q') || '';
  const dateFilter = searchParams.get('datePreset') || 'all';
  
  // Custom date range state with 24-hr time defaults
  const todayStr = useMemo(() => {
    const d = new Date();
    return d.toISOString().split('T')[0];
  }, []);
  const [customStartDate, setCustomStartDate] = useState(searchParams.get('startDate') || todayStr);
  const [customStartTime, setCustomStartTime] = useState(searchParams.get('startTime') || '00:00:00');
  const [customEndDate, setCustomEndDate] = useState(searchParams.get('endDate') || todayStr);
  const [customEndTime, setCustomEndTime] = useState(searchParams.get('endTime') || '23:59:59');

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Helper to update URL params cleanly
  const updateFilter = useCallback((key: string, value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (!value || value === 'all') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
      return next;
    }, { replace: true });
    setPage(1);
  }, [setSearchParams]);

  // Fetch KPI Summary counts from DB
  const fetchSummary = useCallback(async () => {
    try {
      const res = await apiClient.get('/support/audit/summary');
      if (res.data?.data) {
        setSummary(res.data.data);
      }
    } catch (e) {
      console.warn('Failed to load audit summary:', e);
    }
  }, []);

  // Fetch real audit logs from API
  const fetchAuditLogs = useCallback(async (silent = false) => {
    if (!silent) setIsRefreshing(true);
    try {
      const params: Record<string, any> = {
        page,
        limit: pageSize,
      };

      if (activeCategory !== 'all') params.category = activeCategory;
      if (activeLevel !== 'all') params.level = activeLevel;
      if (searchQuery.trim()) params.search = searchQuery.trim();

      if (dateFilter === 'custom') {
        params.startDate = `${customStartDate}T${customStartTime}`;
        params.endDate = `${customEndDate}T${customEndTime}`;
      } else if (dateFilter !== 'all') {
        params.datePreset = dateFilter;
      }

      const response = await apiClient.get('/support/audit', { params });
      const resData = response.data;
      const rawList: AuditLogEntry[] = Array.isArray(resData?.data) ? resData.data : [];

      setLogs(rawList);
      if (resData?.pagination?.total !== undefined) {
        setTotalCount(resData.pagination.total);
      } else {
        setTotalCount(rawList.length);
      }
    } catch (err: any) {
      console.warn('Failed to fetch audit logs from backend:', err?.message || err);
      setLogs([]);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [page, pageSize, activeCategory, activeLevel, searchQuery, dateFilter, customStartDate, customStartTime, customEndDate, customEndTime]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  // Real-time SSE Live Streaming
  useEffect(() => {
    if (!isLiveStreaming) return;

    let eventSource: EventSource | null = null;
    try {
      // Connect to SSE stream
      eventSource = new EventSource('/api/support/audit/stream');

      eventSource.addEventListener('audit', (e) => {
        try {
          const newRecord: AuditLogEntry = JSON.parse(e.data);
          setLogs((prev) => {
            // Deduplicate
            if (prev.some((p) => p.id === newRecord.id)) return prev;
            return [newRecord, ...prev.slice(0, pageSize - 1)];
          });
          setSummary((prev) => ({
            ...prev,
            totalEvents: prev.totalEvents + 1,
            last24hEvents: prev.last24hEvents + 1,
          }));
        } catch (err) {
          console.warn('Error parsing live audit stream record:', err);
        }
      });

      eventSource.onerror = () => {
        // SSE disconnected, fallback to auto-polling every 8s
        eventSource?.close();
      };
    } catch {
      // Fallback polling
    }

    const pollInterval = setInterval(() => {
      fetchAuditLogs(true);
      fetchSummary();
    }, 8000);

    return () => {
      if (eventSource) eventSource.close();
      clearInterval(pollInterval);
    };
  }, [isLiveStreaming, fetchAuditLogs, fetchSummary, pageSize]);

  // Load related events when selecting a log entry
  useEffect(() => {
    if (!selectedLog) {
      setRelatedLogs([]);
      return;
    }

    const rel = logs.filter(
      (l) => l.id !== selectedLog.id && (
        (selectedLog.ticket_id && l.ticket_id === selectedLog.ticket_id) ||
        (selectedLog.request_id && l.request_id === selectedLog.request_id) ||
        (selectedLog.entity_id && l.entity_id === selectedLog.entity_id)
      )
    );
    setRelatedLogs(rel.slice(0, 8));
  }, [selectedLog, logs]);

  // Export functions via real API
  const handleExport = async (format: 'csv' | 'json' | 'xlsx') => {
    try {
      toast.loading(`Generating ${format.toUpperCase()} export...`, { id: 'export-audit' });
      const response = await apiClient.get(`/support/audit/export?format=${format}`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const pad = (n: number) => String(n).padStart(2, '0');
      const now = new Date();
      const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
      link.setAttribute('download', `support-audit-logs_${stamp}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Audit logs exported as ${format.toUpperCase()}`, { id: 'export-audit' });
    } catch {
      toast.error('Failed to export audit logs', { id: 'export-audit' });
    }
  };

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success(`Copied ${field} to clipboard`);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    } catch {
      return dateStr;
    }
  };

  const formatRelativeTime = (dateStr?: string) => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return '—';
      const diffMs = Date.now() - d.getTime();
      if (diffMs < 0) return 'Just now';
      const diffSec = Math.floor(diffMs / 1000);
      const diffMin = Math.floor(diffSec / 60);
      const diffHour = Math.floor(diffMin / 60);
      const diffDay = Math.floor(diffHour / 24);

      if (diffSec < 45) return 'Just now';
      if (diffMin < 60) return `${diffMin}m ago`;
      if (diffHour < 24) return `${diffHour}h ago`;
      if (diffDay < 7) return `${diffDay}d ago`;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return '—';
    }
  };

  const getSeverityBadge = (sev?: string) => {
    const s = (sev || 'info').toLowerCase();
    if (s === 'security') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-red-100 text-red-800 border border-red-300 dark:bg-red-950/60 dark:text-red-200 dark:border-red-800">
          <Shield className="size-3 text-red-600" /> Security
        </span>
      );
    }
    if (s === 'error') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-300">
          <AlertTriangle className="size-3 text-rose-600" /> Error
        </span>
      );
    }
    if (s === 'warn' || s === 'warning') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-300">
          <AlertTriangle className="size-3 text-amber-600" /> Warning
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300">
        <Info className="size-3 text-emerald-600" /> Info
      </span>
    );
  };

  const getCategoryBadge = (category?: string) => {
    const c = (category || 'ticket').toLowerCase();
    const styleMap: Record<string, string> = {
      ticket: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300',
      kb: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300',
      live_chat: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300',
      settings: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/40 dark:text-purple-300',
      portal: 'bg-cyan-50 text-cyan-700 border-cyan-200 dark:bg-cyan-950/40 dark:text-cyan-300',
      auth_rbac: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/40 dark:text-orange-300',
      system: 'bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300',
    };
    const labels: Record<string, string> = {
      ticket: 'Tickets',
      kb: 'Knowledge Base',
      live_chat: 'Live Chat & AI',
      settings: 'Settings',
      portal: 'Portal Activity',
      auth_rbac: 'Auth & RBAC',
      system: 'System Ops',
    };
    return (
      <span className={cn('px-2 py-0.5 rounded text-[11px] font-medium border uppercase tracking-wider', styleMap[c] || styleMap.ticket)}>
        {labels[c] || c}
      </span>
    );
  };

  const categoriesList = [
    { id: 'all', label: 'All Categories' },
    { id: 'ticket', label: 'Tickets' },
    { id: 'kb', label: 'Knowledge Base' },
    { id: 'live_chat', label: 'Live Chat' },
    { id: 'settings', label: 'Settings' },
    { id: 'portal', label: 'Portal' },
    { id: 'auth_rbac', label: 'Auth & RBAC' },
    { id: 'system', label: 'System' },
  ];

  const levelsList = [
    { id: 'all', label: 'All Levels' },
    { id: 'info', label: 'Info' },
    { id: 'warn', label: 'Warnings' },
    { id: 'error', label: 'Errors' },
    { id: 'security', label: 'Security' },
  ];

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <div className="p-4 pb-12 space-y-4 bg-background min-h-full text-foreground text-xs font-sans">
      {/* 1. Header & Live Stream Card */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 bg-card border border-border p-4 rounded-lg shadow-2xs">
        <div className="space-y-1">
          <div className="flex items-center gap-2.5">
            <div className="size-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
              <FileClock className="size-5" />
            </div>
            <div>
              <h1 className="font-display text-page-title tracking-tight text-foreground">
                Audit Log & Compliance Trail
              </h1>
              <p className="text-xs text-muted-foreground">
                Immutable, append-only security logs · Ticket transitions · Settings diffs · AI agent handoffs
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2.5 flex-wrap">
          {/* Live Stream Toggle with SSE */}
          <button
            onClick={() => setIsLiveStreaming(!isLiveStreaming)}
            className={cn(
              "text-xs font-semibold px-3 py-1.5 rounded-lg border flex items-center gap-2 transition-colors cursor-pointer",
              isLiveStreaming
                ? "bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800"
                : "bg-secondary text-muted-foreground border-border hover:text-foreground"
            )}
            title="Real-time live streaming via Server-Sent Events"
          >
            <span className={cn("size-2 rounded-full", isLiveStreaming ? "bg-emerald-500 animate-pulse" : "bg-muted-foreground")} />
            {isLiveStreaming ? 'Live Stream: Active' : 'Live Stream: Paused'}
          </button>

          {/* Refresh button */}
          <button
            onClick={() => { fetchAuditLogs(); fetchSummary(); }}
            disabled={isRefreshing}
            className="p-2 rounded-lg border border-border bg-card hover:bg-secondary text-foreground transition-colors disabled:opacity-50 cursor-pointer shadow-2xs"
            title="Refresh logs now"
          >
            <RefreshCw className={cn("size-3.5", isRefreshing && "animate-spin text-primary")} />
          </button>

          {/* Export Buttons */}
          <button
            onClick={() => handleExport('csv')}
            className="px-3 py-1.5 rounded-lg text-xs font-medium border border-border bg-card hover:bg-secondary text-foreground flex items-center gap-1.5 transition-colors cursor-pointer shadow-2xs"
          >
            <Download className="size-3.5 text-muted-foreground" />
            <span>CSV</span>
          </button>
          <button
            onClick={() => handleExport('json')}
            className="px-3 py-1.5 rounded-lg text-xs font-medium border border-border bg-card hover:bg-secondary text-foreground flex items-center gap-1.5 transition-colors cursor-pointer shadow-2xs"
          >
            <Download className="size-3.5 text-muted-foreground" />
            <span>JSON</span>
          </button>
          <button
            onClick={() => handleExport('xlsx')}
            className="px-3 py-1.5 rounded-lg text-xs font-medium border border-border bg-card hover:bg-secondary text-foreground flex items-center gap-1.5 transition-colors cursor-pointer shadow-2xs"
          >
            <Download className="size-3.5 text-muted-foreground" />
            <span>XLSX</span>
          </button>
        </div>
      </div>

      {/* 2. Top Metric Cards (from DB API) */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <div 
          onClick={() => { updateFilter('category', 'all'); updateFilter('level', 'all'); }}
          className={cn(
            "rounded-lg border bg-card p-3 shadow-2xs cursor-pointer transition-all",
            activeCategory === 'all' && activeLevel === 'all' ? "border-primary/50 ring-1 ring-primary/30" : "border-border hover:border-border/80"
          )}
        >
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Total Events</span>
          <div className="flex items-baseline justify-between mt-1.5">
            <span className="text-xl font-bold tracking-tight text-foreground">{summary.totalEvents}</span>
            <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">Persistent</span>
          </div>
        </div>

        <div 
          onClick={() => updateFilter('category', 'ticket')}
          className={cn(
            "rounded-lg border bg-card p-3 shadow-2xs cursor-pointer transition-all",
            activeCategory === 'ticket' ? "border-primary/50 ring-1 ring-primary/30" : "border-border hover:border-border/80"
          )}
        >
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Ticket Actions</span>
          <div className="flex items-baseline justify-between mt-1.5">
            <span className="text-xl font-bold tracking-tight text-foreground">{summary.categories['ticket'] || 0}</span>
            <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-950/40">Tickets</span>
          </div>
        </div>

        <div 
          onClick={() => updateFilter('level', 'security')}
          className={cn(
            "rounded-lg border bg-card p-3 shadow-2xs cursor-pointer transition-all",
            activeLevel === 'security' ? "border-primary/50 ring-1 ring-primary/30" : "border-border hover:border-border/80"
          )}
        >
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Security Alerts</span>
          <div className="flex items-baseline justify-between mt-1.5">
            <span className="text-xl font-bold tracking-tight text-foreground">{summary.securityAlerts}</span>
            <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200 dark:bg-red-950/40">Sec Audit</span>
          </div>
        </div>

        <div 
          onClick={() => updateFilter('level', 'warn')}
          className={cn(
            "rounded-lg border bg-card p-3 shadow-2xs cursor-pointer transition-all",
            activeLevel === 'warn' ? "border-primary/50 ring-1 ring-primary/30" : "border-border hover:border-border/80"
          )}
        >
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Warnings & Errors</span>
          <div className="flex items-baseline justify-between mt-1.5">
            <span className="text-xl font-bold tracking-tight text-foreground">{summary.warnings + summary.systemErrors}</span>
            <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/40">Needs Review</span>
          </div>
        </div>

        <div 
          onClick={() => updateFilter('datePreset', 'today')}
          className={cn(
            "rounded-lg border bg-card p-3 shadow-2xs cursor-pointer transition-all",
            dateFilter === 'today' ? "border-primary/50 ring-1 ring-primary/30" : "border-border hover:border-border/80"
          )}
        >
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Last 24 Hours</span>
          <div className="flex items-baseline justify-between mt-1.5">
            <span className="text-xl font-bold tracking-tight text-foreground">{summary.last24hEvents}</span>
            <span className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40">Recent</span>
          </div>
        </div>
      </div>

      {/* 3. Filter Controls Card */}
      <div className="bg-card border border-border p-3.5 rounded-lg shadow-2xs space-y-3">
        {/* Category Tabs */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none border-b border-border/60">
          {categoriesList.map((cat) => (
            <button
              key={cat.id}
              onClick={() => updateFilter('category', cat.id)}
              className={cn(
                "px-3 py-1.5 text-xs rounded-md whitespace-nowrap transition-colors cursor-pointer flex items-center gap-1.5",
                activeCategory === cat.id
                  ? "bg-primary text-primary-foreground font-semibold shadow-xs"
                  : "text-muted-foreground hover:text-foreground hover:bg-secondary"
              )}
            >
              <span>{cat.label}</span>
              <span className={cn(
                "px-1.5 py-0.2 rounded-full text-[10px]",
                activeCategory === cat.id
                  ? "bg-primary-foreground/20 text-primary-foreground"
                  : "bg-secondary text-muted-foreground border border-border"
              )}>
                {cat.id === 'all' ? summary.totalEvents : (summary.categories[cat.id] ?? 0)}
              </span>
            </button>
          ))}
        </div>

        {/* Level Filters + Search + Date Controls */}
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Level Tabs */}
            <div className="flex items-center rounded-lg border border-border p-0.5 bg-secondary/40">
              {levelsList.map((lvl) => (
                <button
                  key={lvl.id}
                  onClick={() => updateFilter('level', lvl.id)}
                  className={cn(
                    "px-2.5 py-1 text-xs rounded transition-colors cursor-pointer",
                    activeLevel === lvl.id
                      ? "bg-card text-foreground font-semibold shadow-2xs"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {lvl.label}
                </button>
              ))}
            </div>

            {/* Free Text Search */}
            <div className="relative min-w-[200px] flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => updateFilter('q', e.target.value)}
                placeholder="Search action, ticket ID, IP, actor..."
                className="w-full pl-8 pr-3 py-1.5 text-xs bg-card border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              />
              {searchQuery && (
                <button
                  onClick={() => updateFilter('q', '')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="size-3" />
                </button>
              )}
            </div>
          </div>

          {/* Date & 24hr Time Range Controls */}
          <div className="flex items-center gap-2 flex-wrap text-xs">
            {/* Presets */}
            <div className="flex items-center rounded-lg border border-border p-0.5 bg-secondary/40">
              {[
                { id: 'all', label: 'All Time' },
                { id: 'today', label: 'Today' },
                { id: '7d', label: '7 Days' },
                { id: '30d', label: '30 Days' },
                { id: 'custom', label: 'Custom' },
              ].map((df) => (
                <button
                  key={df.id}
                  onClick={() => updateFilter('datePreset', df.id)}
                  className={cn(
                    "px-2.5 py-1 text-xs rounded transition-colors cursor-pointer",
                    dateFilter === df.id
                      ? "bg-card text-foreground font-semibold shadow-2xs"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  {df.label}
                </button>
              ))}
            </div>

            {/* Custom 24-hr Time Inputs */}
            {dateFilter === 'custom' && (
              <div className="flex items-center gap-1.5 bg-secondary/30 p-1 rounded-lg border border-border">
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => { setCustomStartDate(e.target.value); updateFilter('startDate', e.target.value); }}
                  className="px-2 py-1 bg-card border border-border rounded text-xs text-foreground"
                />
                <input
                  type="time"
                  step="1"
                  value={customStartTime}
                  onChange={(e) => { setCustomStartTime(e.target.value); updateFilter('startTime', e.target.value); }}
                  className="px-1.5 py-1 bg-card border border-border rounded text-xs text-foreground font-mono"
                  title="Start time (24-hour format HH:MM:SS)"
                />
                <span className="text-muted-foreground text-xs">to</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => { setCustomEndDate(e.target.value); updateFilter('endDate', e.target.value); }}
                  className="px-2 py-1 bg-card border border-border rounded text-xs text-foreground"
                />
                <input
                  type="time"
                  step="1"
                  value={customEndTime}
                  onChange={(e) => { setCustomEndTime(e.target.value); updateFilter('endTime', e.target.value); }}
                  className="px-1.5 py-1 bg-card border border-border rounded text-xs text-foreground font-mono"
                  title="End time (24-hour format HH:MM:SS)"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 4. Table */}
      <div className="rounded-lg border border-border bg-card shadow-2xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="border-b border-border bg-secondary/50 text-muted-foreground font-semibold uppercase tracking-wider">
                <th className="py-2.5 px-3.5 w-[140px]">Timestamp (24h)</th>
                <th className="py-2.5 px-3.5 w-[110px]">Category</th>
                <th className="py-2.5 px-3.5 w-[180px]">Action</th>
                <th className="py-2.5 px-3.5 w-[100px]">Level</th>
                <th className="py-2.5 px-3.5 w-[140px]">Target</th>
                <th className="py-2.5 px-3.5">Summary & Details</th>
                <th className="py-2.5 px-3.5 w-[160px]">Actor</th>
                <th className="py-2.5 px-3.5 w-[60px] text-right">Inspect</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-muted-foreground">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <RefreshCw className="size-5 animate-spin text-primary" />
                      <p className="text-xs font-medium">Loading database audit logs...</p>
                    </div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-muted-foreground">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <FileClock className="size-8 text-muted-foreground/40" />
                      <p className="text-xs font-semibold text-foreground">No audit logs found</p>
                      <p className="text-xs text-muted-foreground max-w-sm">
                        {searchQuery || activeCategory !== 'all' || activeLevel !== 'all'
                          ? 'No events matched your current filters. Adjust or clear filters to see more results.'
                          : 'Live audit records will appear here as actions occur in the platform.'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                logs.map((log) => {
                  const isSelected = selectedLog?.id === log.id;
                  const targetLabel = log.target_label || (log.ticket_id ? `#${log.ticket_id}` : `${log.entity_type}:${log.entity_id}`);
                  return (
                    <tr
                      key={log.id}
                      onClick={() => setSelectedLog(log)}
                      className={cn(
                        "hover:bg-secondary/40 transition-colors cursor-pointer group",
                        isSelected && "bg-secondary/70 ring-1 ring-inset ring-primary/30"
                      )}
                    >
                      {/* Timestamp */}
                      <td className="py-2.5 px-3.5 text-muted-foreground whitespace-nowrap">
                        <span className="font-mono text-xs text-foreground font-medium block">
                          {formatRelativeTime(log.ts || log.createdAt)}
                        </span>
                        <span className="text-[11px] text-muted-foreground block font-mono">
                          {formatDate(log.ts || log.createdAt)}
                        </span>
                      </td>

                      {/* Category */}
                      <td className="py-2.5 px-3.5 whitespace-nowrap">
                        {getCategoryBadge(log.category)}
                      </td>

                      {/* Action */}
                      <td className="py-2.5 px-3.5 whitespace-nowrap">
                        <span className="font-semibold text-xs text-foreground block">
                          {log.action}
                        </span>
                        <span className="font-mono text-[10px] text-muted-foreground block truncate max-w-[150px]">
                          {log.entity_type}
                        </span>
                      </td>

                      {/* Level */}
                      <td className="py-2.5 px-3.5 whitespace-nowrap">
                        {getSeverityBadge(log.level)}
                      </td>

                      {/* Target Ticket / Entity */}
                      <td className="py-2.5 px-3.5 whitespace-nowrap">
                        {log.ticket_id ? (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/support/tickets/${encodeURIComponent(log.ticket_id!)}`);
                            }}
                            className="inline-flex items-center gap-1 font-mono text-xs font-semibold text-primary hover:underline"
                            title={`Open ticket ${log.ticket_id}`}
                          >
                            <TicketIcon className="size-3" />
                            {log.ticket_id}
                          </button>
                        ) : (
                          <span className="font-mono text-xs text-muted-foreground truncate max-w-[130px] block" title={targetLabel}>
                            {targetLabel}
                          </span>
                        )}
                      </td>

                      {/* Summary */}
                      <td className="py-2.5 px-3.5">
                        <p className="text-foreground text-xs line-clamp-2 leading-relaxed" title={log.summary}>
                          {log.summary || JSON.stringify(log.details)}
                        </p>
                      </td>

                      {/* Actor */}
                      <td className="py-2.5 px-3.5 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <div className="size-5 rounded-full bg-secondary flex items-center justify-center text-[10px] font-semibold text-muted-foreground shrink-0 border border-border uppercase">
                            {(log.actorName || log.actor_type || 'S').charAt(0)}
                          </div>
                          <div className="truncate max-w-[130px]">
                            <span className="font-medium text-foreground text-xs block truncate" title={log.actorName || log.actor_id || log.actor_type}>
                              {log.actorName || log.actor_id || log.actor_type}
                            </span>
                            <span className="text-[10px] text-muted-foreground block truncate uppercase">
                              {log.actor_type}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Inspect */}
                      <td className="py-2.5 px-3.5 text-right whitespace-nowrap">
                        <span className="inline-flex items-center justify-center size-6 rounded hover:bg-secondary text-muted-foreground group-hover:text-foreground">
                          <ChevronRight className="size-3.5" />
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="p-3 border-t border-border flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            Showing {logs.length} of {totalCount} events
          </span>
          <Pagination
            page={page}
            pageCount={totalPages}
            total={totalCount}
            onPageChange={(p) => setPage(p)}
          />
        </div>
      </div>

      {/* 5. Row Inspector Drawer */}
      {selectedLog && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-2xs transition-all">
          <div className="w-full max-w-xl bg-card h-full shadow-2xl border-l border-border flex flex-col animate-in slide-in-from-right duration-200">
            {/* Header */}
            <div className="p-4 border-b border-border flex items-center justify-between bg-secondary/30">
              <div className="flex items-center gap-2.5">
                <div className="size-7 rounded-md bg-primary/10 border border-primary/20 text-primary flex items-center justify-center font-mono text-xs font-semibold">
                  <Terminal className="size-3.5" />
                </div>
                <div>
                  <h2 className="text-xs font-semibold tracking-tight text-foreground uppercase">
                    Audit Event Inspector
                  </h2>
                  <span className="font-mono text-[10px] text-muted-foreground block">
                    ID: {selectedLog.id}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => copyToClipboard(JSON.stringify(selectedLog, null, 2), 'Audit Event JSON')}
                  className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground"
                  title="Copy complete JSON"
                >
                  {copiedField === 'Audit Event JSON' ? <Check className="size-3.5 text-emerald-600" /> : <Copy className="size-3.5" />}
                </button>
                <button
                  onClick={() => setSelectedLog(null)}
                  className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground"
                >
                  <X className="size-4" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="p-5 space-y-4 overflow-y-auto flex-1 text-xs">
              {/* Event Summary */}
              <div className="p-3.5 rounded-lg border border-border bg-secondary/20 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs font-semibold text-foreground">
                    {selectedLog.action}
                  </span>
                  <div className="flex items-center gap-1.5">
                    {getCategoryBadge(selectedLog.category)}
                    {getSeverityBadge(selectedLog.level)}
                  </div>
                </div>
                <p className="text-foreground text-xs leading-relaxed font-medium">
                  {selectedLog.summary}
                </p>
                <div className="pt-2 border-t border-border/60 text-xs text-muted-foreground flex items-center justify-between font-mono">
                  <span>Timestamp: {formatDate(selectedLog.ts || selectedLog.createdAt)}</span>
                  <span>IP: {selectedLog.ip || '127.0.0.1'}</span>
                </div>
              </div>

              {/* Linked Ticket (if present) */}
              {selectedLog.ticket_id && (
                <div className="p-3.5 rounded-lg border border-primary/20 bg-primary/5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <TicketIcon className="size-4 text-primary" />
                    <div>
                      <span className="text-[10px] font-semibold text-primary uppercase block">Target Ticket</span>
                      <span className="font-mono text-xs font-bold text-foreground">Ticket #{selectedLog.ticket_id}</span>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(`/support/tickets/${encodeURIComponent(selectedLog.ticket_id!)}`)}
                    className="px-2.5 py-1 rounded bg-primary text-primary-foreground text-xs font-medium flex items-center gap-1 hover:bg-primary/90 transition-all shadow-2xs"
                  >
                    Open Ticket <ExternalLink className="size-3" />
                  </button>
                </div>
              )}

              {/* Actor & Session Info */}
              <div className="p-3.5 rounded-lg border border-border bg-card space-y-2">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
                  Actor & Identity Context
                </span>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-[11px] text-muted-foreground block">Actor Name / ID</span>
                    <span className="font-medium text-foreground">{selectedLog.actorName || selectedLog.actor_id || 'System'}</span>
                  </div>
                  <div>
                    <span className="text-[11px] text-muted-foreground block">Actor Type</span>
                    <span className="font-medium text-foreground uppercase">{selectedLog.actor_type}</span>
                  </div>
                  {selectedLog.request_id && (
                    <div className="col-span-2">
                      <span className="text-[11px] text-muted-foreground block">Request ID</span>
                      <span className="font-mono text-xs text-foreground">{selectedLog.request_id}</span>
                    </div>
                  )}
                  {selectedLog.user_agent && (
                    <div className="col-span-2">
                      <span className="text-[11px] text-muted-foreground block">User Agent</span>
                      <span className="font-mono text-[10px] text-foreground break-all">{selectedLog.user_agent}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Before / After Diff */}
              {selectedLog.details && (selectedLog.details.before !== undefined || selectedLog.details.after !== undefined) && (
                <div className="p-3.5 rounded-lg border border-border bg-card space-y-2">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
                    Before / After State Diff
                  </span>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="text-[10px] font-semibold text-rose-600 uppercase block mb-1">State Before</span>
                      <pre className="p-2 rounded bg-rose-50/50 border border-rose-200 text-[11px] font-mono text-rose-900 dark:bg-rose-950/20 dark:text-rose-200 overflow-x-auto max-h-48">
                        {JSON.stringify(selectedLog.details.before || {}, null, 2)}
                      </pre>
                    </div>
                    <div>
                      <span className="text-[10px] font-semibold text-emerald-600 uppercase block mb-1">State After</span>
                      <pre className="p-2 rounded bg-emerald-50/50 border border-emerald-200 text-[11px] font-mono text-emerald-900 dark:bg-emerald-950/20 dark:text-emerald-200 overflow-x-auto max-h-48">
                        {JSON.stringify(selectedLog.details.after || {}, null, 2)}
                      </pre>
                    </div>
                  </div>
                </div>
              )}

              {/* Full Details JSON */}
              <div className="p-3.5 rounded-lg border border-border bg-card space-y-2">
                <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
                  Full Details Payload
                </span>
                <pre className="p-2.5 rounded bg-secondary/80 border border-border text-xs font-mono text-foreground overflow-x-auto max-h-60">
                  {JSON.stringify(selectedLog.details, null, 2)}
                </pre>
              </div>

              {/* Related Events by Ticket or Request ID */}
              {relatedLogs.length > 0 && (
                <div className="p-3.5 rounded-lg border border-border bg-card space-y-2">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
                    Correlated Events ({relatedLogs.length})
                  </span>
                  <div className="space-y-1.5">
                    {relatedLogs.map((rel) => (
                      <div
                        key={rel.id}
                        onClick={() => setSelectedLog(rel)}
                        className="p-2 rounded border border-border/80 bg-secondary/20 hover:bg-secondary/60 cursor-pointer flex items-center justify-between transition-colors"
                      >
                        <div className="truncate max-w-[320px]">
                          <span className="font-semibold text-foreground text-xs block truncate">{rel.action}</span>
                          <span className="text-[10px] text-muted-foreground block truncate">{rel.summary}</span>
                        </div>
                        <span className="font-mono text-[10px] text-muted-foreground whitespace-nowrap">
                          {formatRelativeTime(rel.ts || rel.createdAt)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-3.5 border-t border-border bg-secondary/20 flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                Audit Log verified via PostgreSQL
              </span>
              <button
                onClick={() => setSelectedLog(null)}
                className="px-3 py-1.5 rounded border border-border bg-card hover:bg-secondary text-foreground text-xs font-medium"
              >
                Close Drawer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SupportAuditLog;
